import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router';
import axiosInstance from './axios';

// API/Components
import { AnalysisResultToStateColor, DATA_ERROR_VALUE, STATE_COLOR, isEmpty, validValue, measUnitsConversion, userMetricUnits, formatDateTime } from './enums';

// Material UI
import Button from '@mui/material/Button';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import Grid from '@mui/material/Grid';
import {
	Chart as ChartJS,
	LinearScale,
	CategoryScale,
	TimeScale,
	BarElement,
	PointElement,
	LineElement,
	Legend,
	Tooltip,
	Filler,
	LineController,
	ScatterController,
} from 'chart.js';
import { Chart } from 'react-chartjs-2';
import zoomPlugin from 'chartjs-plugin-zoom';
import 'chartjs-adapter-date-fns';

// Register Chart.JS Plugins
ChartJS.register(
	LinearScale,
	CategoryScale,
	TimeScale,
	BarElement,
	PointElement,
	LineElement,
	Legend,
	Tooltip,
	Filler,
	LineController,
	ScatterController,
	zoomPlugin,
);

// Graph style
const GRAPH_STYLE = {
	AREA: 0,
	LINE: 1,
	POINT: 2
};

export default function SystemDischargeTrendGraph(props) {
	const { trendData, graphType, loading } = props;

	const { locationNum } = useParams();
	
	const chartRef = useRef(null);
	
	const [graphData, setGraphData] = useState([]);
	const [graphSettings, setGraphSettings] = useState({
		limitsEnabled: true,
	});
	const [graphStyle, setGraphStyle] = useState(GRAPH_STYLE.AREA);
	
	const [limitData, setLimitData] = useState([]);
	const [graphOptions, setGraphOptions] = useState({
		maintainAspectRatio: false,
		animation: false,
		options: {
			responsive: true,
		},
		transitions: {
			zoom: {
				animation: {
					duration: 0,
				},
			},
		},
		plugins: {
			legend: {
				display: false,
			},
			tooltip: {
				intersect: false,
				// axis: 'x',
				mode: 'index',
			},
			zoom: {
				pan: {
					enabled: true,
					mode: 'xy',
				},
				limits: {
					x: {
						minRange: 2,
					},
					y: {
						min: 0,
						max: 100,
						minRange: 5,
					}
				},
				zoom: {
					mode: 'y',
					wheel: {
						enabled: true,
						speed: 0.01,
					},
					pinch: {
						enabled: true,
					},
					onZoomStart: (e) => {
						if (typeof e.chart.config?.options?.plugins?.zoom?.zoom === "undefined") {
							return;
						}
						if (e.event.shiftKey === true) {
							e.chart.config.options.plugins.zoom.zoom.mode = "x";
							e.chart.config.options.plugins.zoom.zoom.scaleMode = "x";
							// e.chart.config.options.plugins.zoom.zoom.wheel.speed = 0.01;
						} else {
							e.chart.config.options.plugins.zoom.zoom.mode = "y";
							e.chart.config.options.plugins.zoom.zoom.scaleMode = "y";
							// e.chart.config.options.plugins.zoom.zoom.wheel.speed = 0.01;
						}
					},
				}
			},
		},
	});
	
	// const [isLoading, setIsLoading] = useState(loading);

	// Keep track of the limits data for drawing the limit lines
	const limitDataRef = useRef(null);
	useEffect(() => {
		limitDataRef.current = limitData;
	}, [limitData]);
	
	// Keep track of the graph type for drawing limit lines
	const graphTypeRef = useRef(null);
	useEffect(() => {
		graphTypeRef.current = graphType;
		if (graphType == 0) {
			setGraphStyle(GRAPH_STYLE.LINE);
		}
	}, [graphType]);
	
	// Keep track of the graph settings for drawing limit lines
	const graphSettingsRef = useRef(null);
	useEffect(() => {
		graphSettingsRef.current = graphSettings;
	}, [graphSettings]);
	
	// Get the list of limit data for the specified location id, string number, and unit number
	useEffect(() => {
		axiosInstance.get(`/loc/${locationNum}/limits/discharge`).then(response => {
			setLimitData(response.data);
		});
	}, [locationNum]);

	// Update the graph data sets
	useEffect(() => {
		if (typeof trendData != "undefined" && trendData !== null) {
			const systemData = GetGraphData(trendData.measurements, graphType);
			if (systemData !== undefined && systemData !== null) {
				setGraphData(systemData);
			}
		}
	}, [trendData, graphType, graphSettings, graphStyle, limitData]);

	useEffect(() => {
		const graphScales = SetGraphYScale(graphType);
		if (graphScales !== undefined || graphScales !== null) {

			const firstDate = formatDateTime(trendData.measurements[0].datetime);
			const lastDate = formatDateTime(trendData.measurements[trendData.measurements.length - 1].datetime);

			setGraphOptions((prevState) => {
				return {
					...prevState,
					scales: {
						y: graphScales,
						x: {
							title: {
								display: false,
							},
							suggestedMin: firstDate,
							suggestedMax: lastDate,
						}
					}
				}
			});
		}
	}, [limitData, graphType]);
	
	useEffect(() => {
		const graphZoom = SetGraphZoom(graphType);
		if (graphZoom !== undefined && !isEmpty(graphZoom)) {

			setGraphOptions((prevState) => {
				return {
					...prevState,
					plugins: {
						...prevState.plugins,
						zoom: {
							...prevState.plugins.zoom,
							limits: {
								...prevState.plugins.zoom.limits,
								x: {
									min: "original",
									max: "original",
								},
								y: graphZoom,
							},
						},
					},
				}
			});
		}
	}, [limitData, graphType]);

	const graphPlugins = [
		{
			afterDraw: chart => {				
				
				// Draw a vertical dashed line at the tooltip's x position
				let ctx = chart.ctx;
				if (chart.tooltip?._active?.length) {
					let x = chart.tooltip._active[0].element.x;
					let yAxis = chart.scales.y;
					ctx.save();
					ctx.beginPath();
					ctx.setLineDash([5, 5]);
					ctx.moveTo(x, yAxis.top);
					ctx.lineTo(x, yAxis.bottom);
					ctx.lineWidth = 1;
					ctx.strokeStyle = 'rgba(0, 0, 255, 0.9)';
					ctx.stroke();
					ctx.restore(); 
				}
			}
		},
		{
			afterDatasetDraw: chart => {
				let graphType = graphTypeRef.current;
				let graphSettings = graphSettingsRef.current;
				let limitData = limitDataRef.current;

				if (limitData != undefined && limitData != null && !isEmpty(limitData)) {
					// If enabled, draw a horizontal dashed line at the various limit values
					if (graphSettings.limitsEnabled) {
						const limits = GetLimitsData(limitData, graphType);
						if (limits != undefined && limits != null && limits.length > 0) {
							limits.forEach((limit) => {
								DrawHorizontalDashedLine(chart, limit.value, limit.color);
							});
						}
					}
				}
			}
		}
	];

	function DrawHorizontalDashedLine(chart, yValue, color) {
		if (chart !== undefined && yValue !== undefined && yValue > DATA_ERROR_VALUE.INVALID_VALUE) {
			let ctx = chart.ctx;
			let xAxis = chart.scales.x;
			let y = chart.scales.y.getPixelForValue(yValue);

			ctx.save();
			ctx.beginPath();
			ctx.setLineDash([5, 5]);
			ctx.moveTo(xAxis.left, y);
			ctx.lineTo(xAxis.right, y);
			ctx.lineWidth = 1;
			ctx.strokeStyle = color;
			ctx.stroke();
			ctx.restore(); 
		}
	}

	function GetLimitsData(limitData, graphType) {
		let critHiLimit = DATA_ERROR_VALUE.INVALID_VALUE;
		let critLoLimit = DATA_ERROR_VALUE.INVALID_VALUE;
		
		if (limitData != undefined && limitData != null && !isEmpty(limitData) &&
			graphType != undefined && graphType != null) {
			switch(graphType) {
				case 0:
					critHiLimit = limitData.current.discharge;
					critLoLimit = limitData.volt;
					break;
				case 1:
					critLoLimit = limitData.volt;
					break;
				case 2:
					critHiLimit = limitData.current.discharge;
					break;
				case 3:
					critHiLimit = measUnitsConversion(limitData.temp.hi_crit);
					critLoLimit = measUnitsConversion(limitData.temp.lo_crit);
					break;
				default:
					critHiLimit = DATA_ERROR_VALUE.INVALID_VALUE;
					critLoLimit = DATA_ERROR_VALUE.INVALID_VALUE;
					break;
			}
		}

		// Get the associated limit colors
		let hiCritColor = AnalysisResultToStateColor(4);
		let loCritColor = AnalysisResultToStateColor(5);

		let limitSets = [];
		if (critHiLimit !== undefined && critHiLimit > DATA_ERROR_VALUE.INVALID_VALUE) {
			limitSets.push({
				value: critHiLimit,
				color: hiCritColor
			});
		}

		if (critLoLimit !== undefined && critLoLimit > DATA_ERROR_VALUE.INVALID_VALUE) {
			limitSets.push({
				value: critLoLimit,
				color: loCritColor
			});
		}
		
		return limitSets;
	}

	function SetGraphYScale(graphType) {
		
		if (trendData != undefined && !isEmpty(trendData)) {

			let max = -10000;
			let min = 10000;
			let title = "";

			switch (graphType) {
				case 0:  // Voltage/Current/Power
					let maxCurrent = validValue(trendData.stats.current.max, 5000);
					let maxVolt = validValue(trendData.stats.voltage.max, 600);
					
					max = Math.max(maxCurrent, maxVolt) * 1.15;
					min = 0;
					title = "Calculated Power (KWh)";
					break;
				case 1:  // Voltage
					let upperVolt = validValue(trendData.stats.voltage.max, 600);
					max = upperVolt * 1.15;

					let lowerVolt = validValue(trendData.stats.voltage.min, 0);
					min = lowerVolt * 0.80;

					title = "System Voltage (VDC)";
					break;

				case 2:  // Current
					let upperCurrent = validValue(trendData.stats.current.max, 5000);
					max = upperCurrent * 1.15;

					let lowerCurrent = validValue(trendData.stats.current.min, 0);
					min = lowerCurrent * 0.80;

					title = "System Current (A)";
					break;

				case 3:  // Temperature
					let upperTemp = validValue(trendData.stats.temperature.max, 120);
						max = measUnitsConversion(upperTemp) * 1.15;

					let lowerTemp = validValue(trendData.stats.temperature.min, -40);
						min = measUnitsConversion(lowerTemp) * 0.80;
					
					let units = userMetricUnits() ? 'C' : 'F';
					title = 'Ambient Temperature (°' + units + ')';
					break;

				default:
					maxCurrent = validValue(trendData.stats.current.max, 5000);
					maxVolt = validValue(trendData.stats.voltage.max, 600);
					
					max = Math.max(maxCurrent, maxVolt) * 1.15;
					min = 0;
					title = "Calculated Power (KWh)";
					break;
			}

			if (max > 0 && min != 10000) {
				return {
					max: max,
					min: min,
					title: {
						display: true,
						text: title,
						font: {
							family: "Saira, sans-serif",
							size: 18,
							weight: 600,
						},
					},
				};
			}
		}

		return {};
	}
	
	function SetGraphZoom(graphType) {

		if (trendData != undefined && !isEmpty(trendData) &&
			limitData != undefined && !isEmpty(limitData)) {

			let max = 0;
			let min = 0;
			let minRange = 0;

			switch (graphType) {
				case 0:  // Voltage/Current/Power
					let maxCurrent = validValue(trendData.stats.current.max, 5000);
					let maxVolt = validValue(trendData.stats.voltage.max, 600);
					let upperCurrentLimit = validValue(limitData.current.discharge, 0);
					
					max = Math.max(maxCurrent, maxVolt, upperCurrentLimit) * 1.15;
					min = 0;
					minRange = 1
					break;

				case 1:  // Voltage
					let upperVolt = validValue(trendData.stats.voltage.max, 600);
					let voltLimit = validValue(limitData.volt, 0);
					
					max = Math.max(upperVolt, voltLimit) * 1.15;
					min = 0;
					minRange = 1
					break;

				case 2:  // Current
					let upperCurrent = validValue(trendData.stats.current.max, 10000);
					let currentLimit = validValue(limitData.current.discharge, 0);
					
					max = Math.max(upperCurrent, currentLimit) * 1.15;
					min = 0;
					minRange = 5
					break;

				case 3:  // Temperature
					let upperTemp = validValue(trendData.stats.temperature.max, 120);
					let tempHiLimit = validValue(limitData.temp.hi_crit, 35);

					let upperTempConverted = measUnitsConversion(upperTemp);
					let tempHiLimitConverted = measUnitsConversion(tempHiLimit);

					max = Math.max(upperTempConverted, tempHiLimitConverted) * 1.15;
					min = -40;
					minRange = 5;
					break;

				default:  // Voltage/Current/Power
					maxCurrent = validValue(trendData.stats.current.max, 5000);
					maxVolt = validValue(trendData.stats.voltage.max, 600);
					upperCurrentLimit = validValue(limitData.current.discharge, 0);
					
					max = Math.max(maxCurrent, maxVolt, upperCurrentLimit) * 1.15;
					min = 0;
					minRange = 1
					break;
			}

			return {
				max: max,
				min: min,
				minRange: minRange,
			};
		}

		return {};
	}

	function GetMeasurementInfo(measurement, graphType) {
		let value, analysis = 0;
		switch(graphType) {
			case 0:
				value = measurement.power.measurement?.toFixed(3);
				analysis = (measurement.power.analysis_valid) ? 
					measurement.power.analysis_result : 0;
				break;
			case 1:
				value = measurement.voltage.measurement?.toFixed(3);
				analysis = (measurement.voltage.analysis_valid) ? 
					measurement.voltage.analysis_result : 0;
				break;
			case 2:
				value = measurement.current.measurement?.toFixed(3);
				analysis = (measurement.current.analysis_valid) ? 
					measurement.current.analysis_result : 0;
				break;
			case 3:
				let temp = measurement.temperature.measurement?.toFixed(3);
				value = measUnitsConversion(temp);
				analysis = (measurement.temperature.analysis_valid) ? 
					measurement.temperature.analysis_result : 0;
				break;
			default:
				value = measurement.voltage.measurement?.toFixed(3);
				analysis = (measurement.voltage.analysis_valid) ? 
					measurement.voltage.analysis_result : 0;
				break;
		}
		value = (value !== null && value !== undefined && value > DATA_ERROR_VALUE.INVALID_VALUE) ? value : DATA_ERROR_VALUE.INVALID_VALUE;
		let analysisColor = AnalysisResultToStateColor(analysis);

		return {value, analysisColor};
	}

	function GetGraphTypeText(graphType) {
		switch(graphType) {
			case 0:
				return "Calculated Power";
			case 1:
				return "System Voltage";
			case 2:
				return "System Current";
			case 3:
				return "Ambient Temperature";
			default:
				return "Calculated Power";
		}
	}

	function GetGraphData(measurementsTrendData, graphType) {
		// Pick the units to graph based on the selected string
		if (measurementsTrendData && measurementsTrendData.length > 0) {
			
			// Get the measurement data for the specified measurement data and graph type
			function GetSingleTrendData(measurementData, graphType, graphStyle, colorOverride = null) {
	
				let barColor = [];
				let borderColor = [];
				let chartData = [];
	
				const graphTypeText = GetGraphTypeText(graphType);
				let graphFill = colorOverride !== null ? false : graphStyle == GRAPH_STYLE.AREA;
	
				measurementData.forEach((measurement) => {
					const { value, analysisColor } = GetMeasurementInfo(measurement, graphType);
					
					let setColor = colorOverride !== null ? colorOverride : analysisColor;
					let label = formatDateTime(measurement.datetime);

					if (value !== DATA_ERROR_VALUE.INVALID_VALUE) {
						barColor.push(setColor + "77");
						borderColor.push(setColor);
						chartData.push({x: label, y: value});
					}
				});

				return {
					type: graphStyle == GRAPH_STYLE.POINT ? 'scatter' : 'line',
					label: graphTypeText,
					backgroundColor: barColor,
					borderColor: borderColor,
					segment: {
						borderColor: (ctx) => (barColor[ctx.p1DataIndex]),
						backgroundColor: (ctx) => (barColor[ctx.p1DataIndex]),
					},
					data: chartData,
					fill: graphFill,
					order: 10,
				}
			}
	
			let dataSets = [];
			if (graphType == 0) {
				dataSets.push(GetSingleTrendData(measurementsTrendData, 0, graphStyle, STATE_COLOR.WITHIN_LIMITS));
				dataSets.push(GetSingleTrendData(measurementsTrendData, 1, graphStyle, STATE_COLOR.MAINTENANCE));
				dataSets.push(GetSingleTrendData(measurementsTrendData, 2, graphStyle, STATE_COLOR.CRITICAL));
			} else {
				dataSets.push(GetSingleTrendData(measurementsTrendData, graphType, graphStyle));
			}
	
			return {
				labels: measurementsTrendData.map((measurement) => formatDateTime(measurement.datetime)),
				datasets: dataSets,
			}
		}
	
		return null;
	}

	const handleLimitMarkersToggle = () => {
		setGraphSettings((prevState) => ({
			...prevState,
			limitsEnabled: !graphSettings.limitsEnabled})
		);
	};

	const handleGraphResetZoom = () => {
		chartRef.current.resetZoom();
	};

	const handleGraphStyle = (event, newStyle) => {
		if (newStyle !== null) {
			setGraphStyle(newStyle);
		}
	};

	return (
		<div
			style={{
				width: '100%',
				height: '99%',
				display: 'flex',
				flexDirection: 'column',
				position: 'relative',
			}}
		>
			<div style={{ height: '100%', width: '100%' }}>
				{ (graphData !== undefined && graphData.length != 0) &&
					<Chart
						ref={chartRef}
						type='line'
						options={graphOptions}
						data={graphData}
						plugins = {graphPlugins}
					/>
				}
			</div>
			<Grid container spacing={1}>
				<Grid item xs alignItems="left">
					<ToggleButtonGroup
						value={[]}
						onChange={() => {}}
						aria-label="graph markers shown"
						size='small'
						sx={{ padding: '6px' }}
					>
						<ToggleButton
							value="limitMarkers"
							selected={graphSettings.limitsEnabled}
							onClick={handleLimitMarkersToggle}
						>
							Limits
						</ToggleButton>
					</ToggleButtonGroup>
				</Grid>
				<Grid item xs alignItems="center">
					<Button
						variant="contained"
						startIcon={<CenterFocusStrongIcon />}
						onClick={handleGraphResetZoom}
						sx={{
							marginY: 1,
							backgroundColor: 'white',
							border: '1px solid lightgray',
							color: 'black',
							":hover": {
								backgroundColor: 'lightgray',
								boxShadow: 'none',
							}

						}}
					>
						Reset View
					</Button>
				</Grid>
				<Grid item>
					<ToggleButtonGroup
						value={graphStyle}
						onChange={handleGraphStyle}
						exclusive
						aria-label="graph marker type"
						size='small'
						sx={{ padding: '6px' }}
					>
						<ToggleButton
							value={GRAPH_STYLE.AREA}
							aria-label="Area Graph Style"
							disabled={graphType == 0}
						>
							Area
						</ToggleButton>
						<ToggleButton
							value={GRAPH_STYLE.LINE}
							aria-label="Line Graph Style"
						>
							Line
						</ToggleButton>
						<ToggleButton
							value={GRAPH_STYLE.POINT}
							aria-label="Point Graph Style"
						>
							Point
						</ToggleButton>
					</ToggleButtonGroup>
				</Grid>
			</Grid>
		</div>
	);
}